import React, { useState, useEffect } from 'react';
import { ChevronDown, Send, CheckCircle, XCircle, Loader, AlertCircle } from 'lucide-react';
import { leaveAPI, leaveConfigAPI, employeeAPI, establishmentAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useAuth, useActiveEstId } from './AuthContext';
import { getPrimaryRole } from '../utils/permissions';
import { getLocalToday } from '../utils/dateUtils';
import DateInput from './DateInput';
import EstablishmentCard from './EstablishmentCard';
import './ApplyLeave.css';

const LEAVE_TYPES = [
  { value: 'annual',      label: 'Annual Leave' },
  { value: 'sick',        label: 'Sick Leave' },
  { value: 'casual',      label: 'Casual Leave' },
  { value: 'maternity',   label: 'Maternity Leave' },
  { value: 'paternity',   label: 'Paternity Leave' },
  { value: 'emergency',   label: 'Emergency Leave' },
  { value: 'bereavement', label: 'Bereavement Leave' },
  { value: 'study',       label: 'Study Leave' },
  { value: 'unpaid',      label: 'Unpaid Leave' },
  { value: 'other',       label: 'Other' },
];

// Maps leave type values to balance fields returned by leaveConfigAPI.getBalance
const BALANCE_MAP = {
  sick:   'sl_balance',
  casual: 'cl_balance',
  annual: 'el_balance',
};

const BALANCE_CARDS = [
  { key: 'sl_balance', icon: '🏥', label: 'Sick Leave',   cls: 'al-bal-sl' },
  { key: 'el_balance', icon: '🌴', label: 'Earned Leave', cls: 'al-bal-el' },
  { key: 'cl_balance', icon: '📅', label: 'Casual Leave', cls: 'al-bal-cl' },
];

const genReqNumber = () => {
  const y = new Date().getFullYear();
  const r = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
  return `LVR-${y}-${r}`;
};

const getInitials = (first = '', last = '') =>
  `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase() || 'E';

const EMPTY_FORM = {
  leave_type: 'annual',
  start_date: '',
  end_date: '',
  total_days: '0',
  half_day: false,
  reason: '',
  remarks: '',
};

// ── Shared leave form ───────────────────────────────────────────────
function LeaveForm({ profile, leaveBalance, balanceLoading, onSuccess }) {
  const [form, setForm]           = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast]         = useState(null);

  // Auto-dismiss toast after 5s
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const set = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (['start_date', 'end_date', 'half_day'].includes(field)) {
        if (next.start_date && next.end_date) {
          const diff = Math.ceil(
            Math.abs(new Date(next.end_date) - new Date(next.start_date)) / 86400000
          ) + 1;
          next.total_days = (next.half_day && next.start_date === next.end_date)
            ? '0.5' : diff.toString();
        }
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    // Balance check first — always enforce regardless of button disabled state
    const balKey = BALANCE_MAP[form.leave_type];
    if (balKey && leaveBalance) {
      const available = parseFloat(leaveBalance[balKey] || 0);
      const requested = parseFloat(form.total_days || 0);
      const typeName = LEAVE_TYPES.find(t => t.value === form.leave_type)?.label || form.leave_type;
      if (available <= 0) {
        setToast({ type: 'error', msg: `No ${typeName} balance available. You have 0 days remaining.` });
        return;
      }
      if (requested > available) {
        setToast({ type: 'error', msg: `Insufficient balance. Requested ${requested} day(s) but only ${available} day(s) of ${typeName} remaining.` });
        return;
      }
    }
    if (!form.leave_type || !form.start_date || !form.end_date || !form.reason?.trim()) {
      setToast({ type: 'error', msg: 'Please fill in Leave Type, Start Date, End Date and Reason.' });
      return;
    }
    if (new Date(form.end_date) < new Date(form.start_date)) {
      setToast({ type: 'error', msg: 'End date cannot be before start date.' });
      return;
    }
    setIsSubmitting(true);
    setToast(null);
    const reqNo = genReqNumber();
    try {
      await leaveAPI.apply({
        leaveRequestNumber: reqNo,
        employeeId:         profile.employee_id,
        employeeName:       `${profile.first_name} ${profile.last_name}`,
        establishmentId:    profile.establishment_id || null,
        departmentId:       profile.department || null,
        managerId:          'MANAGER-001',
        approverId:         'MANAGER-001',
        approverName:       'Manager',
        leaveType:          form.leave_type,
        startDate:          form.start_date,
        endDate:            form.end_date,
        daysCount:          parseFloat(form.total_days),
        halfDay:            form.half_day,
        reason:             form.reason,
        remarks:            form.remarks || null,
        leaveStatus:        'pending',
      });
      notifySavedAndReload(`Leave submitted successfully — ${reqNo}`);
    } catch (err) {
      setToast({ type: 'error', msg: err.message || 'Failed to submit leave application.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const days     = parseFloat(form.total_days || 0);
  const hasDates = form.start_date && form.end_date;

  const balKey = BALANCE_MAP[form.leave_type];
  const availableBalance = balKey && leaveBalance ? parseFloat(leaveBalance[balKey] || 0) : null;
  const balanceInsufficient = availableBalance !== null && availableBalance <= 0;

  return (
    <>
      {toast && (
        <div className={`al-toast ${toast.type}`}>
          {toast.type === 'success'
            ? <CheckCircle size={18} style={{ flexShrink: 0 }} />
            : <XCircle    size={18} style={{ flexShrink: 0 }} />}
          <span>{toast.msg}</span>
        </div>
      )}

      {/* Leave Balance Cards */}
      {(leaveBalance || balanceLoading) && (
        <div className="al-card al-balance-section">
          <p className="al-card-label">Leave Balance</p>
          {balanceLoading ? (
            <div className="al-balance-loading"><div className="al-spinner" style={{ borderColor: 'rgba(99,102,241,0.2)', borderTopColor: '#6366f1' }} /> Loading balance…</div>
          ) : (
            <div className="al-balance-row">
              {BALANCE_CARDS.map(({ key, icon, label, cls }) => (
                <div key={key} className={`al-bal-card ${cls}`}>
                  <div className="al-bal-icon">{icon}</div>
                  <div className="al-bal-body">
                    <div className="al-bal-num">{leaveBalance?.[key] ?? 0}</div>
                    <div className="al-bal-label">{label}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="al-card">
        <p className="al-card-label">Leave Details</p>
        <div className="al-form-grid">

          <div className="al-field">
            <label className="al-label">Leave Type <span>*</span></label>
            <div className="al-select-wrap">
              <select value={form.leave_type} onChange={e => set('leave_type', e.target.value)} className="al-select">
                {LEAVE_TYPES.map(t => {
                  const bk = BALANCE_MAP[t.value];
                  const bal = bk && leaveBalance ? parseFloat(leaveBalance[bk] || 0) : null;
                  const noBalance = bal !== null && bal <= 0;
                  return (
                    <option key={t.value} value={t.value}>
                      {t.label}{noBalance ? ' (no balance)' : bal !== null ? ` (${bal} days)` : ''}
                    </option>
                  );
                })}
              </select>
              <ChevronDown className="al-chevron" size={16} />
            </div>
          </div>

          <div className="al-field al-balance-hint-field">
            {availableBalance !== null && (
              <div className={`al-balance-hint ${balanceInsufficient ? 'al-balance-hint-warn' : 'al-balance-hint-ok'}`}>
                {balanceInsufficient
                  ? <><AlertCircle size={14} /> No balance available for this leave type</>
                  : <><CheckCircle size={14} /> Available: <strong>{availableBalance}</strong> day{availableBalance !== 1 ? 's' : ''}</>}
              </div>
            )}
          </div>

          <div className="al-field">
            <label className="al-label">Start Date <span>*</span></label>
            <DateInput value={form.start_date} onChange={e => set('start_date', e.target.value)} className="al-input" min={`${new Date().getFullYear()}-01-01`} />
          </div>

          <div className="al-field">
            <label className="al-label">End Date <span>*</span></label>
            <DateInput value={form.end_date} onChange={e => set('end_date', e.target.value)} className="al-input" min={form.start_date || `${new Date().getFullYear()}-01-01`} />
          </div>

          <label className="al-halfday">
            <input type="checkbox" checked={form.half_day} onChange={e => set('half_day', e.target.checked)} />
            <div>
              <div className="al-halfday-text">Half Day</div>
              <div className="al-halfday-sub">Only applies when start and end date are the same</div>
            </div>
          </label>

          <div className="al-field al-field-full">
            <label className="al-label">Reason for Leave <span>*</span></label>
            <textarea value={form.reason} onChange={e => set('reason', e.target.value)} className="al-textarea" placeholder="Briefly describe the reason for your leave" rows={3} />
          </div>

          <div className="al-field al-field-full">
            <label className="al-label">Additional Remarks</label>
            <textarea value={form.remarks} onChange={e => set('remarks', e.target.value)} className="al-textarea" placeholder="Optional — handover notes, contact details, etc." rows={2} />
          </div>
        </div>

        {hasDates && (
          <div className="al-summary">
            <div className="al-summary-item">
              <div className="al-summary-key">Duration</div>
              <div className="al-summary-days">{days}</div>
              <div className="al-summary-days-label">{days === 1 ? 'day' : 'days'}</div>
            </div>
            <div className="al-summary-item">
              <div className="al-summary-key">Leave Type</div>
              <div className="al-summary-val">{LEAVE_TYPES.find(t => t.value === form.leave_type)?.label}</div>
            </div>
            <div className="al-summary-item">
              <div className="al-summary-key">Status after submit</div>
              <div className="al-summary-val pending">Pending Approval</div>
            </div>
          </div>
        )}

        {balanceInsufficient && (
          <div className="al-balance-block-msg">
            <AlertCircle size={15} />
            Insufficient leave balance — you cannot apply for this leave type.
          </div>
        )}
        <div className="al-submit-row">
          <button className="al-btn-submit" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting
              ? <><div className="al-spinner" /> Submitting…</>
              : <><Send size={16} /> Submit Leave Application</>}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Employee self-service ───────────────────────────────────────────
function EmployeeApplyLeave({ user }) {
  const [profile, setProfile]           = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [error, setError]               = useState('');
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  useEffect(() => {
    employeeAPI.getMe()
      .then(data => {
        setProfile(data);
        if (data?.employee_id) {
          setBalanceLoading(true);
          leaveConfigAPI.getBalance(data.employee_id)
            .then(lb => setLeaveBalance(lb))
            .catch(() => setLeaveBalance(null))
            .finally(() => setBalanceLoading(false));
        }
      })
      .catch(err => setError(err.message || 'Could not load profile'))
      .finally(() => setProfileLoading(false));
  }, []);

  if (profileLoading) return (
    <div className="al-page">
      <div className="al-state-center">
        <Loader size={28} className="al-spinner-lg" />
        <p>Loading your profile…</p>
      </div>
    </div>
  );

  if (!profile) return (
    <div className="al-page">
      <div className="al-state-center al-state-warn">
        <AlertCircle size={28} />
        <p>No employee record is linked to your account ({user?.email}).<br />
           Please contact your administrator.</p>
      </div>
    </div>
  );

  return (
    <div className="al-page">
      <div className="al-hero">
        <div className="al-hero-avatar">{getInitials(profile.first_name, profile.last_name)}</div>
        <div className="al-hero-info">
          <h1 className="al-hero-name">{profile.first_name} {profile.last_name}</h1>
          <div className="al-hero-meta">
            {profile.position && <span>{profile.position}</span>}
            {profile.department && <><span className="al-hero-dot">·</span><span>{profile.department}</span></>}
            {profile.employment_type && <><span className="al-hero-dot">·</span><span className="al-hero-badge">{profile.employment_type}</span></>}
          </div>
          <div className="al-hero-id">Employee ID: {profile.employee_id}</div>
        </div>
      </div>
      <LeaveForm profile={profile} leaveBalance={leaveBalance} balanceLoading={balanceLoading} />
    </div>
  );
}

// ── Admin / Manager view ────────────────────────────────────────────
function AdminApplyLeave() {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  const [establishments, setEstablishments] = useState([]);
  const [employees, setEmployees]           = useState([]);
  const [selectedEst, setSelectedEst]       = useState(lockedEstId || '');
  const [selectedEmpId, setSelectedEmpId]   = useState('');
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [leaveBalance, setLeaveBalance]     = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [estLoading, setEstLoading]         = useState(true);
  const [empLoading, setEmpLoading]         = useState(false);

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) setSelectedEst(lockedEstId);
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lockedEstId) { setEstLoading(false); return; }
    establishmentAPI.getAll()
      .then(r => setEstablishments(r.establishments || []))
      .finally(() => setEstLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedEst) { setEmployees([]); setSelectedEmpId(''); setSelectedProfile(null); return; }
    setEmpLoading(true);
    employeeAPI.getByEstablishment(selectedEst)
      .then(r => setEmployees(r.employees || []))
      .catch(() => setEmployees([]))
      .finally(() => setEmpLoading(false));
    setSelectedEmpId('');
    setSelectedProfile(null);
  }, [selectedEst]);

  const handleEmployeeChange = (empId) => {
    setSelectedEmpId(empId);
    setLeaveBalance(null);
    if (!empId) { setSelectedProfile(null); return; }
    const emp = employees.find(e => String(e.employee_id) === String(empId));
    if (emp) {
      setSelectedProfile(emp);
      setBalanceLoading(true);
      leaveConfigAPI.getBalance(emp.employee_id)
        .then(lb => setLeaveBalance(lb))
        .catch(() => setLeaveBalance(null))
        .finally(() => setBalanceLoading(false));
    }
  };

  return (
    <div className="al-page">
      <div className="al-toolbar">
        <h1 className="al-page-title">Apply Leave</h1>
      </div>

      {!lockedEstId && (
        <EstablishmentCard
          establishments={establishments}
          value={selectedEst}
          onChange={e => setSelectedEst(e.target.value)}
          loading={estLoading}
        />
      )}

      {selectedEst && (
        <div className="est-card-wrap">
          <label className="est-label">Employee</label>
          <div className="est-dropdown">
            <select
              className="est-select"
              value={selectedEmpId}
              onChange={e => handleEmployeeChange(e.target.value)}
              disabled={empLoading}
            >
              <option value="">{empLoading ? 'Loading…' : 'Select employee'}</option>
              {employees.map(e => (
                <option key={e.employee_id} value={e.employee_id}>
                  {e.first_name} {e.last_name} ({e.employee_id})
                </option>
              ))}
            </select>
            <ChevronDown className="est-chevron" size={16} />
          </div>
        </div>
      )}

      {selectedProfile && (
        <>
          <div className="al-hero" style={{ marginBottom: '1rem' }}>
            <div className="al-hero-avatar">{getInitials(selectedProfile.first_name, selectedProfile.last_name)}</div>
            <div className="al-hero-info">
              <h1 className="al-hero-name">{selectedProfile.first_name} {selectedProfile.last_name}</h1>
              <div className="al-hero-meta">
                {selectedProfile.position && <span>{selectedProfile.position}</span>}
                {selectedProfile.department && <><span className="al-hero-dot">·</span><span>{selectedProfile.department}</span></>}
              </div>
              <div className="al-hero-id">Employee ID: {selectedProfile.employee_id}</div>
            </div>
          </div>
          <LeaveForm profile={selectedProfile} leaveBalance={leaveBalance} balanceLoading={balanceLoading} onSuccess={() => { setSelectedEmpId(''); setSelectedProfile(null); setLeaveBalance(null); }} />
        </>
      )}
    </div>
  );
}

// ── Main export ─────────────────────────────────────────────────────
export default function ApplyLeave() {
  const { user } = useAuth();
  const role = getPrimaryRole(user);
  if (role === 'SuperAdmin' || role === 'ClientAdmin' || role === 'MultiClientAdmin') return <AdminApplyLeave />;
  return <EmployeeApplyLeave user={user} />;
}
